import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { MdAdd, MdDelete } from 'react-icons/md';
import { CampaignType } from '../../constants/postType.constants';
import useStore from '../../stores/store';
import { getGivingOptionUnitsForLocale } from '../Projects/projectFormSchema';
import ClickableTooltip from '../Styled/ClickableTooltip';
import StyledContainedButton from '../Styled/StyledContainedButton';
import StyledInput from '../Styled/styledInput';
import StyledSelect, { StyledOption } from '../Styled/styledSelect';
import TextArea from '../next-ui/TextArea';
// import Typography from '../next-ui/Typography';
import Input from '../next-ui/Input';

const OpportunityCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'grayed',
})(({ theme, grayed }) => ({
  width: '100%',
  '& .MuiCardContent-root': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'start',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: grayed ? theme.palette.background.innerGray : 'white',
  },
}));

const NumberedBullet = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.innerGray,
  borderRadius: '50%',
  width: 36,
  height: 36,
  marginTop: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const QuantityRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: theme.spacing(2),
  alignItems: 'start',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

function GivingLevelForm(props) {
  const { type, edit } = props;
  const location = useStore((state) => state.user.profile.location);
  const { currencySymbol, country } = location;
  const quantityPrefix = type === CampaignType.FundRaise ? currencySymbol : '';
  const {
    control,
    register,
    formState: { errors },
    getValues,
  } = useFormContext();
  const {
    fields: givingLevelFields,
    append: appendGivingLevel,
    remove: removeGivingLevel,
  } = useFieldArray({
    control: control,
    name: 'givingLevels',
  });

  const givingLevelErrors = errors.givingLevels || [];

  const typeSpecificProps = {
    [CampaignType.FundRaise]: {
      title: 'Name',
      quantity: 'Donation Amount',
      description: 'Description',
      example: {
        title: 'Need: Sponsor 1 month education for 10 kids',
        quantity: `Donation Amount: ${currencySymbol}100`,
        description:
          'Description: Support and sponsor education (Math, science etc.) for 10 kids for a month.',
      },
      helpTexts: {
        title:
          'Please provide a name for the giving opportunity. For example, sponsor 5 days of training for 10 women in Kenya.',
        description:
          'Explain the details of this giving opportunity. For example, Support and sponsor 5 days of training (basic business skills, bookkeeping, health & nutrition, human rights, leadership, personal development and reproductive health) for 10 women. ',
        quantity:
          'Please provide the donation amount for this giving opportunity.',
      },
    },
    [CampaignType.CollectGoods]: {
      title: 'Name of goods or supplies to collect',
      quantity: 'Quantity',
      description: 'Description',
      unit: 'Unit',
      // pricePerUnit: 'Approximate Price',
      example: {
        title: 'Need: Donate School School bags for kids age 6-10 years.',
        quantity: 'Quantity: 100',
        description:
          'Description: We are collecting school bags for kids age 6-10. Please make sure the bags are not torn or damaged.',
        unit: 'Unit: Nos',
        // pricePerUnit: `Approximate Price: ${currencySymbol}100`,
      },
      helpTexts: {
        title:
          'Please provide the name of the item that you need the donors to donate. For example, "Blankets", "Medicine kit" etc.',
        description:
          'Please provide a detailed specification of the item and explain how this will help your beneficiery. For example, "Preferably unused blankets for adults. This will help the homeless stay warm during the winter."',
        quantity: 'Please provide the number of the items that you need.',
        unit: "What's the unit of measurement? For example, Kg, Lb etc.",
        // pricePerUnit:
        //   'Please provide the approximate price of the item so that you can help quantify the value of the donation to your donor.',
      },
    },
  };

  const deleteGivingLevel = (index) => {
    removeGivingLevel(index);
  };

  const addGivingLevel = () => {
    appendGivingLevel({
      title: '',
      quantity: '',
      description: '',
      unit: null,
      // pricePerUnit: null,
      pricePerUnit: 1,
    });
    setTimeout(() => {
      const addLevelButton = document.getElementById('add-giving-level-button');
      addLevelButton?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  return (
    <Stack width="100%" gap={2}>
      <OpportunityCard grayed>
        <CardContent>
          <Typography variant="body1" className="italic">
            EXAMPLE
          </Typography>
          <Controller
            control={control}
            name="exampleTitle"
            render={({ field }) => (
              <Input
                {...field}
                disabled
                // label="Example Title"
                value={typeSpecificProps[type].example.title}
                className="bg-[#f2f2f2] italic"
              />
            )}
          />
          <Controller
            control={control}
            name="exampleDescription"
            render={({ field }) => (
              <Input
                {...field}
                disabled
                multiline
                // label="Example Description"
                value={typeSpecificProps[type].example.description}
                className="bg-[#f2f2f2] italic"
              />
            )}
          />
          <QuantityRow>
            <Controller
              control={control}
              name="exampleQuantity"
              render={({ field }) => (
                <Input
                  {...field}
                  disabled
                  value={`${quantityPrefix} ${typeSpecificProps[type].example.quantity}`}
                  className="bg-[#f2f2f2] italic"
                />
              )}
            />
            {type === CampaignType.CollectGoods && (
              <>
                <Controller
                  control={control}
                  name="exampleUnit"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled
                      value={typeSpecificProps[type].example.unit}
                      className="bg-[#f2f2f2] italic"
                    />
                  )}
                />
                {/* <Controller
                  control={control}
                  name="examplePricePerUnit"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled
                      value={typeSpecificProps[type].example.pricePerUnit}
                      className="bg-[#f2f2f2] italic"
                    />
                  )}
                /> */}
              </>
            )}
          </QuantityRow>
        </CardContent>
      </OpportunityCard>

      <Stack alignItems="start" justifyContent="start" gap={2} width="100%">
        {givingLevelFields.map((givingLevel, index) => (
          <OpportunityCard id={`giving-level-row-${index}`} key={index}>
            <CardContent>
              <Stack
                alignItems="start"
                justifyContent="start"
                width="100%"
                gap={2}>
                <Box width="100%">
                  <Controller
                    control={control}
                    name={`givingLevels[${index}].title`}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label={typeSpecificProps[type].title}
                        placeholder={typeSpecificProps[
                          type
                        ].title.toUpperCase()}
                        error={givingLevelErrors[index]?.title?.message}
                        required
                      />
                    )}
                  />
                </Box>
                <Box width="100%">
                  <Controller
                    control={control}
                    name={`givingLevels[${index}].description`}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        label="Description"
                        placeholder={typeSpecificProps[
                          type
                        ].description.toUpperCase()}
                        error={givingLevelErrors[index]?.description?.message}
                        required
                      />
                    )}
                  />
                </Box>

                <QuantityRow>
                  <div className="w-full">
                    <Controller
                      control={control}
                      name={`givingLevels[${index}].quantity`}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label={typeSpecificProps[type].quantity}
                          placeholder={typeSpecificProps[
                            type
                          ].quantity.toUpperCase()}
                          error={givingLevelErrors[index]?.quantity?.message}
                          required
                        />
                      )}
                    />
                  </div>
                  {type === CampaignType.CollectGoods && (
                    <>
                      <div className="w-full">
                        <Controller
                          control={control}
                          name={`givingLevels[${index}].unit`}
                          render={({ field: { onChange, value } }) => (
                            <Stack position="relative">
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="start"
                                // gap={0.5}
                                // mb={0.5}
                              >
                                <Typography
                                  className="font-semibold"
                                  variant="body2"
                                  color="textSecondary">
                                  UNIT *
                                </Typography>

                                {/* <ClickableTooltip
                                  message={
                                    typeSpecificProps[type].helpTexts.unit
                                  }
                                /> */}
                              </Stack>

                              <StyledSelect
                                className="mt-2"
                                // {...field}
                                required
                                onChange={(e, selectedVal) => {
                                  onChange(selectedVal);
                                }}
                                value={
                                  edit
                                    ? givingLevel?.unit || 'Nos'
                                    : getValues(`givingLevels[${index}].unit`)
                                }
                                className={`${
                                  givingLevelErrors[index]?.unit?.message
                                    ? '!border-error'
                                    : ''
                                } pb-5`}
                                register={register(
                                  `givingLevels[${index}].unit`,
                                )}>
                                {getGivingOptionUnitsForLocale(country).map(
                                  (opt) => (
                                    <StyledOption
                                      key={opt.value}
                                      value={opt.value}
                                      label={opt.label}>
                                      <Typography
                                        variant="body2"
                                        component="div">
                                        {opt.label}
                                      </Typography>
                                    </StyledOption>
                                  ),
                                )}
                              </StyledSelect>
                              <Typography
                                variant="caption"
                                color={
                                  givingLevelErrors[index]?.unit?.message
                                    ? 'error'
                                    : 'transparent'
                                }>
                                {givingLevelErrors[index]?.unit?.message}
                              </Typography>
                            </Stack>
                          )}
                        />
                      </div>
                      {/* <div className="w-full">
                        <Controller
                          control={control}
                          name={`givingLevels[${index}].pricePerUnit`}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label={typeSpecificProps[type].pricePerUnit}
                              placeholder={typeSpecificProps[
                                type
                              ].pricePerUnit.toUpperCase()}
                              error={
                                givingLevelErrors[index]?.pricePerUnit?.message
                              }
                              required
                            />
                          )}
                        />
                      </div> */}
                    </>
                  )}
                </QuantityRow>
                <Box ml="auto">
                  <IconButton
                    type="button"
                    onClick={() => deleteGivingLevel(index)}
                    color="error">
                    <MdDelete />
                  </IconButton>
                </Box>
              </Stack>
            </CardContent>
          </OpportunityCard>
        ))}
      </Stack>

      <Box ml="auto" my={2}>
        <StyledContainedButton
          type="button"
          invertColors
          onClick={addGivingLevel}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="start"
            gap={0.5}>
            <MdAdd />
            Giving Opportunity
          </Stack>
        </StyledContainedButton>
      </Box>
    </Stack>
  );
}

GivingLevelForm.propTypes = {
  type: PropTypes.oneOf([CampaignType.FundRaise, CampaignType.CollectGoods])
    .isRequired,
};

export default GivingLevelForm;
